import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  useTheme,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Home,
  LifeBuoy,
  Mail,
  MessageCircle,
  AlertCircle,
  Clock,
  Wallet,
  ChevronRight,
} from 'lucide-react';
import { LAYOUT } from '../../../constants/layout';
import { ROUTES } from '../../../config/constants/routes';
import { useAuth } from '../../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedOrganisationName } from '../../../store/slices/organizationSlice';
import { CustomListItemText } from './CustomListItemText';
import { RootState } from '../../../store';
import { useDashboard } from '../../../hooks/dashboard/useDashboard';
import { AUTH_CONSTANTS } from '../../../config/constants';

interface HeaderProps {
  isDrawerOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ isDrawerOpen }) => {
  const { isAuthenticated, role, isAdmin } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const isUser = role === AUTH_CONSTANTS.ROLES.USER;
  const companyName = useSelector(selectSelectedOrganisationName);
  const isTrial = selectedOrganization?.isTrailEnabled;
  const trialEndDate = new Date(selectedOrganization?.subscriptionEndDate || '');

  const { dashboardData, isLoading: isDashboardLoading, refetch } = useDashboard();

  // Calculate days remaining with null check
  const today = new Date();
  const diffTime = trialEndDate?.getTime() - today.getTime();
  const trialDaysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const showTrialBanner = isTrial && trialDaysRemaining <= 7 && !isUser;
  const trialBannerHeight = 35;
  const toolbarHeight = showTrialBanner ? LAYOUT.TOOLBAR_HEIGHT - 15 : LAYOUT.TOOLBAR_HEIGHT;

  // Format date for display with null check
  const formattedEndDate =
    trialEndDate?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) || '';

  // State for dropdown menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Get badge color based on remaining days
  const getTrialBadgeColor = () => {
    if (!trialDaysRemaining) return theme.palette.info;
    if (trialDaysRemaining <= 3) return theme.palette.error;
    if (trialDaysRemaining <= 7) return theme.palette.warning;
    return theme.palette.info;
  };

  const trialBadgeColor = getTrialBadgeColor();

  const handleSubscriptionClick = () => {
    navigate('/dashboard/subscriptions');
  };

  // Safe default for credits
  const DEFAULT_CREDITS = dashboardData?.credits?.remaining || 0;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        height: toolbarHeight + (showTrialBanner ? trialBannerHeight : 0),
        width: { sm: `calc(100% - 64px)` },
        left: '64px',
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Toolbar
        sx={{
          minHeight: `${toolbarHeight}px !important`,
          height: `${toolbarHeight}px`,
          pl: 0,
          py: 0,
        }}
      >
        {/* Trial banner - displays above header when in trial mode */}
        {/* Trial banner now sits above the toolbar to avoid overlap */}
        {/* Low Credit Banner */}
        {!isDashboardLoading && isAuthenticated && DEFAULT_CREDITS < 5 && (
          <Box
            sx={{
              position: 'absolute',
              top: isTrial && trialDaysRemaining <= 7 && !isUser ? `${LAYOUT.TOOLBAR_HEIGHT}px` : 0,
              left: 0,
              right: 0,
              backgroundColor: `${theme.palette.warning.main}15`,
              borderBottom: `1px solid ${theme.palette.warning.main}30`,
              padding: '2px 0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1099,
              transform: 'translateY(-100%)',
              animation: 'slideDownCredits 0.5s forwards',
              '@keyframes slideDownCredits': {
                '0%': { transform: 'translateY(-100%)' },
                '100%': { transform: 'translateY(0)' },
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.warning.main,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <AlertCircle size={12} />
              {isTrial ? (
                <>
                  Your trial credits are running low ({DEFAULT_CREDITS} remaining).
                  <Box component="span" sx={{ ml: 0.5 }}>
                    Please&nbsp;
                    <Button
                      size="small"
                      onClick={handleSubscriptionClick}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0,
                        color: theme.palette.warning.dark,
                        textDecoration: 'underline',
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      subscribe and top up
                    </Button>
                    .
                  </Box>
                </>
              ) : (
                <>
                  You’re running low on credits ({DEFAULT_CREDITS} remaining).
                  <Button
                    size="small"
                    onClick={handleSubscriptionClick}
                    sx={{
                      ml: 1,
                      fontSize: '0.7rem',
                      py: 0,
                      color: theme.palette.warning.dark,
                      textDecoration: 'underline',
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Top up now
                  </Button>
                </>
              )}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isDashboardLoading ? (
                <Skeleton variant="text" width={150} height={40} />
              ) : (
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {companyName || 'Loading...'}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Trial Status and Upgrade Button */}
            {isTrial && !isUser && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mr: 1,
                  position: 'relative',
                }}
              >
                {isDashboardLoading ? (
                  <Skeleton variant="rectangular" width={200} height={40} />
                ) : (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      {trialDaysRemaining <= 3 ? (
                        <AlertCircle size={16} color={trialBadgeColor.main} />
                      ) : (
                        <Clock size={16} color={trialBadgeColor.main} />
                      )}
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: theme.palette.text.secondary,
                          fontWeight: 400,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Trial ends{' '}
                        <Box component="span" sx={{ color: trialBadgeColor.main, fontWeight: 500 }}>
                          {formattedEndDate}
                        </Box>
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSubscriptionClick}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        backgroundColor: trialBadgeColor.main,
                        color: '#fff',
                        borderRadius: '50%',
                        minWidth: 32,
                        width: 32,
                        height: 32,
                        padding: 0,
                        fontSize: '0.8rem',
                        '&:hover': {
                          backgroundColor: trialBadgeColor.dark,
                        },
                      }}
                    >
                      <ChevronRight size={18} />
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* Wallet Credit Display */}
            {isAuthenticated && (
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '12px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {isDashboardLoading ? (
                  <Skeleton variant="rectangular" width={100} height={24} />
                ) : (
                  <Tooltip title="Available credits">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Wallet size={20} color={theme.palette.primary.main} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                      >
                        {DEFAULT_CREDITS} Credits
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}

            <Button
              onMouseEnter={handleMenuOpen}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: theme.palette.text.primary,
                backgroundColor: `${theme.palette.secondary.main}08`,
                borderRadius: '8px',
                padding: '6px 12px',
                '&:hover': {
                  backgroundColor: `${theme.palette.secondary.main}15`,
                },
              }}
            >
              <LifeBuoy size={18} style={{ marginRight: 8 }} />
              Support
            </Button>

            {/* Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                onMouseLeave: handleMenuClose,
              }}
              sx={{
                mt: 1,
                '& .MuiPaper-root': {
                  border: `1px solid ${theme.palette.action.selected}`,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  mt: 1.5,
                },
              }}
            >
              <MenuItem
                onClick={() =>
                  window.open('https://github.com/cacxi/cacxi-knowledge-base/wiki', '_blank')
                }
                sx={{ py: 1.5 }}
              >
                <BookOpen size={18} style={{ marginRight: 12, color: theme.palette.info.main }} />
                <CustomListItemText
                  primaryText={'Documentation'}
                  secondaryText={'Setup, how-to, and troubleshooting guides'}
                />
              </MenuItem>

              {/* <MenuItem
                onClick={() => (window.location.href = 'mailto:contact@cacxi.com')}
                sx={{ py: 1.5 }}
              >
                <Mail size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                <CustomListItemText
                  primaryText={'Contact Us'}
                  secondaryText={'Reach out for support or inquiries'}
                />
              </MenuItem> */}

              <MenuItem
                onClick={() =>
                  window.open('https://github.com/cacxi/cacxi-issues-portal/issues', '_blank')
                }
                sx={{ py: 1.5 }}
              >
                <MessageCircle
                  size={18}
                  style={{ marginRight: 12, color: theme.palette.secondary.main }}
                />
                <CustomListItemText
                  primaryText={'Find an issue?'}
                  secondaryText={'Encountered an issue, raise it to us!'}
                />
              </MenuItem>
            </Menu>

            {isAuthenticated && isAdmin && (
              <Button
                startIcon={<Home size={18} />}
                onClick={() => navigate(ROUTES.ROOT)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  backgroundColor: `${theme.palette.primary.main}08`,
                  borderRadius: '8px',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: `${theme.palette.primary.main}15`,
                  },
                }}
              >
                Organizations
              </Button>
            )}
          </Box>
        </Box>
      </Toolbar>
      {showTrialBanner && (
        <Box
          sx={{
            width: '100%',
            backgroundColor: `${trialDaysRemaining <= 3 ? theme.palette.error.main : theme.palette.warning.main}15`,
            borderBottom: `1px solid ${trialDaysRemaining <= 3 ? theme.palette.error.main : theme.palette.warning.main}30`,
            height: `${trialBannerHeight}px`,
            px: 1.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              lineHeight: 1.1,
              color:
                trialDaysRemaining <= 3 ? theme.palette.error.main : theme.palette.warning.main,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <AlertCircle size={12} />
            Your trial expires on {formattedEndDate} ({trialDaysRemaining} days left).
            <Button
              size="small"
              onClick={handleSubscriptionClick}
              sx={{
                fontSize: '0.65rem',
                py: 5,
                px: 0,
                minWidth: 'auto',
                minHeight: 'auto',
                height: 'auto',
                color:
                  trialDaysRemaining <= 3 ? theme.palette.error.dark : theme.palette.warning.dark,
                textDecoration: 'underline',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Upgrade now
            </Button>
          </Typography>
        </Box>
      )}
    </AppBar>
  );
};

export default Header;
